import { useEffect } from 'react'
import { Box, Grid } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import { InputField } from 'src/components/shared/ui/form/fields'
import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import SwitchField from 'src/components/shared/ui/form/fields/SwitchField'
import TextareaField from 'src/components/shared/ui/form/fields/TextareaField'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { Orden as OrdenMaster, TipoCuantiaOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { Orden, TipoCuantia } from 'src/interfaces/Catalog'

function Datos(): JSX.Element {
  const { t } = useTranslation()
  const { setFieldValue, values, validateForm } = useFormikContext<EscritosFormValues>()
  const { allowFloatNumber } = useNumberValidations()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )

  const datosReconvencion = values[FormField.DATOS_RECONVENCION]
  const datosTipoCuantia: TipoCuantia | null = values[FormField.DATOS_TIPO_CUANTIA]

  function showTipoCuantia(): JSX.Element | null {
    if (datosReconvencion === 'Sí') {
      return (
        <Grid item xs={6} md={4}>
          <AutoCompleteField
            id={FormField.DATOS_TIPO_CUANTIA}
            items={[
              { codigo: 'I', descripcion: TipoCuantiaOpciones.INDETERMINADA },
              { codigo: 'D', descripcion: TipoCuantiaOpciones.DETERMINADA }
            ]}
            label={t('form.field.datos_tipo_cuantia')}
            itemName="descripcion"
            itemKey="codigo"
            required
          />
        </Grid>
      )
    }
    return null
  }

  function showCuantiaReconvencion(): JSX.Element | null {
    if (datosTipoCuantia && datosTipoCuantia.codigo === 'D') {
      return (
        <Grid item xs={6} md={4}>
          <InputField
            id={FormField.DATOS_CUANTIA_RECONVENCION}
            label={t('form.field.datos_cuantia_reconvencion')}
            clearable
            currencyType
            required
            onChange={allowFloatNumber}
            maxLength={16}
          />
        </Grid>
      )
    }
    return null
  }

  function showSocialFields(): JSX.Element | null {
    const options = [OrdenMaster.SOCIAL, OrdenMaster.CIVIL]

    if (orden.value && options.includes(orden.value.orden)) {
      return (
        <>
          <Box sx={{ marginBottom: '16px' }}>
            <SwitchField
              id={FormField.DATOS_RECONVENCION}
              label={t('form.field.datos_reconvencion')}
              valueOff="No"
              valueOn="Sí"
            />
          </Box>
          {(datosReconvencion === 'Sí' ||
            (datosTipoCuantia && datosTipoCuantia.codigo === 'D')) && (
            <Grid container spacing={2}>
              {showTipoCuantia()}
              {showCuantiaReconvencion()}
            </Grid>
          )}
        </>
      )
    }
    return null
  }

  useEffect(() => {
    if (datosReconvencion === 'No') {
      setFieldValue(FormField.DATOS_TIPO_CUANTIA, null)
      setFieldValue(FormField.DATOS_CUANTIA_RECONVENCION, '')
    }
  }, [datosReconvencion, setFieldValue, datosTipoCuantia])

  useEffect(() => {
    if (datosTipoCuantia?.descripcion !== TipoCuantiaOpciones.DETERMINADA) {
      setFieldValue(FormField.DATOS_CUANTIA_RECONVENCION, '')
    }
  }, [datosTipoCuantia, setFieldValue])

  useEffect(() => {
    const options = [OrdenMaster.SOCIAL, OrdenMaster.CIVIL]

    if (!(orden.value && options.includes(orden.value.orden)) && datosReconvencion === 'Sí') {
      setFieldValue(FormField.DATOS_RECONVENCION, 'No', false)
    }

    // eslint-disable-next-line
  }, [orden])

  return (
    <FieldsetGroup legend={t('form.legend.datos')}>
      {showSocialFields()}
      <TextareaField
        id={FormField.DATOS_OBSERVACIONES}
        label={t('form.field.observaciones')}
        maxLength={1000}
        disabled={!orden.value}
      />
      <SwitchField
        id={FormField.DATOS_URGENTE}
        label={t('form.field.urgente')}
        disabled={!orden.value}
      />
    </FieldsetGroup>
  )
}

export default Datos
